import { RefPane, RefPrefs } from "../shared/types";

// the refs deck: the layouts and sorts on offer, what a fresh person starts with, and the maths behind the
// masonry and rows layouts. the view owns the dom, this file only knows numbers and names

export interface LayoutOption {
  id: string;
  label: string;
  // rects in a 38 by 28 box, drawn as the layout's little picture
  glyph: string;
  // the feed's own single column, the board has no use for it
  feedOnly?: boolean;
}

export const LAYOUTS: LayoutOption[] = [
  { id: "grid", label: "Grid", glyph: '<rect x="2" y="2" width="10" height="10"/><rect x="14" y="2" width="10" height="10"/><rect x="26" y="2" width="10" height="10"/><rect x="2" y="16" width="10" height="10"/><rect x="14" y="16" width="10" height="10"/><rect x="26" y="16" width="10" height="10"/>' },
  { id: "masonry", label: "Masonry", glyph: '<rect x="2" y="2" width="10" height="14"/><rect x="2" y="18" width="10" height="8"/><rect x="14" y="2" width="10" height="8"/><rect x="14" y="12" width="10" height="14"/><rect x="26" y="2" width="10" height="11"/><rect x="26" y="15" width="10" height="11"/>' },
  { id: "rows", label: "Rows", glyph: '<rect x="2" y="2" width="14" height="10"/><rect x="18" y="2" width="18" height="10"/><rect x="2" y="14" width="19" height="12"/><rect x="23" y="14" width="13" height="12"/>' },
  { id: "big", label: "Big grid", glyph: '<rect x="2" y="2" width="16" height="24"/><rect x="20" y="2" width="16" height="24"/>' },
  { id: "wall", label: "Wall", glyph: '<rect x="2" y="2" width="6" height="6"/><rect x="10" y="2" width="6" height="6"/><rect x="18" y="2" width="6" height="6"/><rect x="26" y="2" width="6" height="6"/><rect x="2" y="10" width="6" height="6"/><rect x="10" y="10" width="6" height="6"/><rect x="18" y="10" width="6" height="6"/><rect x="26" y="10" width="6" height="6"/><rect x="2" y="18" width="6" height="6"/><rect x="10" y="18" width="6" height="6"/><rect x="18" y="18" width="6" height="6"/><rect x="26" y="18" width="6" height="6"/>' },
  { id: "posts", label: "Posts", glyph: '<rect x="8" y="2" width="22" height="24"/>', feedOnly: true }
];

export interface SortOption {
  id: string;
  label: string;
  hint: string;
  // grouped sorts put a heading over each group
  grouped?: boolean;
}

export const SORTS: SortOption[] = [
  { id: "random", label: "Shuffle", hint: "random order, rolled once while it is open" },
  { id: "new", label: "Newest", hint: "latest saved first" },
  { id: "old", label: "Oldest", hint: "earliest saved first" },
  { id: "most", label: "Most tagged", hint: "refs with the most tags first" },
  { id: "undescribed", label: "Undescribed first", hint: "what has no description yet comes first" },
  { id: "tags", label: "By tag", hint: "grouped under the most used tag", grouped: true },
  { id: "author", label: "By author", hint: "grouped A to Z", grouped: true },
  { id: "month", label: "By month", hint: "grouped by the month you saved them", grouped: true },
  { id: "type", label: "By type", hint: "stills, gifs and clips apart", grouped: true }
];

export const SIZES = ["s", "m", "l"];
export const KINDS = ["all", "stills", "gifs", "clips"];
export const DESCRIBED = ["all", "yes", "no"];

export function defaultPrefs(): RefPrefs {
  return {
    board: { layout: "grid", size: "m", sort: "new", reverse: false },
    feed: { layout: "posts", size: "m", sort: "random", reverse: false },
    kind: "all",
    described: "all",
    favorites: false
  };
}

function cleanPane(raw: Partial<RefPane> | undefined, fallback: RefPane, feed: boolean): RefPane {
  const layouts = LAYOUTS.filter((item) => feed || !item.feedOnly).map((item) => item.id);
  return {
    layout: raw?.layout && layouts.includes(raw.layout) ? raw.layout : fallback.layout,
    size: raw?.size && SIZES.includes(raw.size) ? raw.size : fallback.size,
    sort: raw?.sort && SORTS.some((item) => item.id === raw.sort) ? raw.sort : fallback.sort,
    reverse: typeof raw?.reverse === "boolean" ? raw.reverse : fallback.reverse
  };
}

// whatever came from settings is checked field by field, so an old or hand edited value can't break the view
export function cleanPrefs(raw: Partial<RefPrefs> | null | undefined, fallback: RefPrefs = defaultPrefs()): RefPrefs {
  return {
    board: cleanPane(raw?.board, fallback.board, false),
    feed: cleanPane(raw?.feed, fallback.feed, true),
    kind: raw?.kind && KINDS.includes(raw.kind) ? raw.kind : fallback.kind,
    described: raw?.described && DESCRIBED.includes(raw.described) ? raw.described : fallback.described,
    favorites: typeof raw?.favorites === "boolean" ? raw.favorites : Boolean(fallback.favorites)
  };
}

const TARGET: Record<string, number> = { s: 110, m: 170, l: 280 };
const WALL_TARGET: Record<string, number> = { s: 52, m: 72, l: 104 };
const BIG_TARGET: Record<string, number> = { s: 190, m: 260, l: 400 };
const ROW_HEIGHT: Record<string, number> = { s: 90, m: 130, l: 190 };

export function gapFor(layout: string): number {
  return layout === "wall" ? 3 : layout === "big" || layout === "posts" ? 10 : 6;
}

// how many columns fit, never fewer than one
export function columnsFor(layout: string, size: string, width: number): number {
  const gap = gapFor(layout);
  const target = (layout === "wall" ? WALL_TARGET : layout === "big" ? BIG_TARGET : TARGET)[size] || 170;
  return Math.max(1, Math.floor((Math.max(width, 1) + gap) / (target + gap)));
}

// rows lays pictures out at one height and stretches each row to the full width, a little taller on wide screens
export function rowHeightFor(size: string, width: number): number {
  const base = ROW_HEIGHT[size] || 130;
  return width >= 900 ? Math.round(base * 1.5) : base;
}

// ratios are width over height. thumbs keep the picture's shape, so one look at a loaded tile is enough
const RATIO_KEY = "ref-ratios";
const ratioMap = new Map<string, number>();
let ratioTimer = 0;

function loadRatios(): void {
  try {
    const saved = JSON.parse(localStorage.getItem(RATIO_KEY) || "{}") as Record<string, number>;
    for (const [id, value] of Object.entries(saved)) if (value > 0) ratioMap.set(id, value);
  } catch {
    // no saved shapes yet, tiles start portrait and settle as they load
  }
}
loadRatios();

export function ratioOf(id: string): number {
  return ratioMap.get(id) || 0.75;
}

export function ratioKnown(id: string): boolean {
  return ratioMap.has(id);
}

// true when the shape is new or moved, so a layout that depends on it should settle again
export function learnRatio(id: string, width: number, height: number): boolean {
  if (!width || !height) return false;
  const next = Math.round(Math.min(4, Math.max(0.25, width / height)) * 100) / 100;
  const old = ratioMap.get(id);
  if (old && Math.abs(old - next) / old < 0.02) return false;
  ratioMap.set(id, next);
  window.clearTimeout(ratioTimer);
  ratioTimer = window.setTimeout(() => {
    try {
      // keeps the newest few thousand
      const keep = [...ratioMap.entries()].slice(-4000);
      localStorage.setItem(RATIO_KEY, JSON.stringify(Object.fromEntries(keep)));
    } catch {
      // private mode
    }
  }, 800);
  return true;
}

// each item goes to the shortest column so far, which keeps the order reading left to right
export function masonryColumns(ids: string[], count: number, extra = 0): string[][] {
  const columns: string[][] = Array.from({ length: count }, () => []);
  const heights = new Array<number>(count).fill(0);
  for (const id of ids) {
    let best = 0;
    for (let at = 1; at < count; at++) if (heights[at] < heights[best]) best = at;
    columns[best].push(id);
    heights[best] += 1 / ratioOf(id) + extra;
  }
  return columns;
}
